// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Beeline — the pure game state. No DOM, no canvas, no clock, no network: a guess
// in, a new state out. This is what the tests, the sim and the solver all drive.

import { bearing, haversine, proximityPct } from './geo';
import { country, type Country } from './data';
import { guessableIds, modeOf, type Mode } from './modes';
import { mysteryFor } from './seed';

export interface Guess {
  id: string;
  name: string;
  cont: string;
  /** Great-circle distance from the guessed country to the mystery (km). */
  km: number;
  /** Bearing from the guess toward the mystery, degrees clockwise from north. */
  bearing: number;
  /** Proximity percentage 0..100 (100 == solved). */
  pct: number;
  correct: boolean;
}

export type EndReason = 'solved' | 'out' | 'timeout';

export interface GameState {
  modeId: string;
  seed: number;
  mysteryId: string;
  maxGuesses: number;
  guesses: Guess[];
  solved: boolean;
  over: boolean;
  reason: EndReason | null;
}

export function createGame(mode: Mode, seed: number): GameState {
  const mystery = mysteryFor(mode, seed);
  return {
    modeId: mode.id,
    seed: seed >>> 0,
    mysteryId: mystery.id,
    maxGuesses: mode.guesses,
    guesses: [],
    solved: false,
    over: false,
    reason: null,
  };
}

export function mysteryCountry(state: GameState): Country {
  return country(state.mysteryId)!;
}

export function guessesLeft(state: GameState): number {
  return Math.max(0, state.maxGuesses - state.guesses.length);
}

export function alreadyGuessed(state: GameState, id: string): boolean {
  return state.guesses.some((g) => g.id === id);
}

/** Build the clue a guess would reveal — pure, exported for the solver + tests. */
export function clueFor(mysteryId: string, guessId: string): Omit<Guess, 'name' | 'cont'> {
  const m = country(mysteryId)!;
  const g = country(guessId)!;
  const correct = guessId === mysteryId;
  const km = correct ? 0 : haversine(g.c, m.c);
  return {
    id: guessId,
    km,
    bearing: correct ? 0 : bearing(g.c, m.c),
    pct: correct ? 100 : proximityPct(km),
    correct,
  };
}

/**
 * Apply a guess. Pure: returns a new state, or the SAME state (unchanged) if the
 * guess is rejected — after the round is over, not in the mode's guess pool, an
 * unknown id, or a repeat. Callers compare identity to know if anything happened.
 */
export function guessCountry(state: GameState, guessId: string): GameState {
  if (state.over) return state;
  const mode = modeOf(state.modeId);
  if (!guessableIds(mode).has(guessId)) return state;
  if (alreadyGuessed(state, guessId)) return state;
  const g = country(guessId);
  if (!g) return state;

  const clue = clueFor(state.mysteryId, guessId);
  const guess: Guess = { ...clue, name: g.name, cont: g.cont };
  const guesses = [...state.guesses, guess];

  if (guess.correct) {
    return { ...state, guesses, solved: true, over: true, reason: 'solved' };
  }
  if (guesses.length >= state.maxGuesses) {
    return { ...state, guesses, over: true, reason: 'out' };
  }
  return { ...state, guesses };
}

/** End the round because the race clock expired. */
export function markTimeout(state: GameState): GameState {
  if (state.over) return state;
  return { ...state, over: true, reason: 'timeout' };
}

/** The closest guess so far (highest pct), for ranking an unsolved result. */
export function bestGuess(state: GameState): Guess | null {
  if (!state.guesses.length) return null;
  return state.guesses.reduce((a, b) => (b.pct > a.pct ? b : a));
}
